import { useApolloClient } from '@apollo/client';
import InfoBox from 'components/infoBox/InfoBox';
import FilterChips from 'components/filterChips/FilterChips';
import KpiCard from 'components/kpiCard/KpiCard';
import { AlertIcon, InfoIcon } from 'components/svgs';
import { GenericTable } from 'components/table/IdmTable';
import { useCapacityPlanDeliveriesQuery } from 'core/graphql/CapacityPlanning/CapacityPlanDeliveries.generated';
import {
  CapacityPlanDeliveryRow,
  CapacityPlanRunDetail,
} from 'core/graphql/CapacityPlanning/models';
import { useSetCapacityPlanDeliveryAddressMutation } from 'core/graphql/CapacityPlanning/SetCapacityPlanDeliveryAddress.generated';
import { useSetCapacityPlanDeliveryCoordinatesMutation } from 'core/graphql/CapacityPlanning/SetCapacityPlanDeliveryCoordinates.generated';
import {
  CapacityPlanDeliveryFilter,
  CapacityPlanRunStatus,
} from 'core/graphql/types';
import usePolledQuery from 'core/hooks/usePolledQuery';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createDeliveriesTableModel,
  DeliveryEditor,
} from './DeliveriesTableConfiguration';
import {
  DeliveryAddressDraft,
  emptyAddressDraft,
  isBlankAddress,
} from './DeliveryAddress';
import { parseDeliveryCoordinates } from './DeliveryCoordinates';
import DeliveryIssuesCard from './DeliveryIssuesCard';

export const deliveriesPageSize = 100;

/** How often the geocoding progress is refreshed while rows are pending. */
const geocodingPollIntervalMs = 5000;

/** Queries a saved pin or address refreshes once the server accepted it. */
const affectedQueries = [
  'CapacityPlanDeliveries',
  'CapacityPlanRun',
  'CapacityPlanRuns',
  'CapacityPlanDeliveryIssues',
];

/** Queries one polling tick refreshes: rows, run counters and issue groups. */
const polledQueries = [
  'CapacityPlanDeliveries',
  'CapacityPlanRun',
  'CapacityPlanDeliveryIssues',
];

const isRunBusy = (run: CapacityPlanRunDetail) =>
  run.status === CapacityPlanRunStatus.Queued ||
  run.status === CapacityPlanRunStatus.Computing;

const share = (value: number, total: number) => (total > 0 ? value / total : 0);

export interface DeliveriesTabProps {
  run: CapacityPlanRunDetail;
  canEdit: boolean;
}

/**
 * Validation and geocoding outcome of one Run: counters, grouped issues and the
 * uploaded rows, with the inline coordinate editor for the pins the Address
 * Processing Flow could not resolve and the address editor for invalid or
 * failed-geocode rows.
 */
const DeliveriesTab = ({ run, canEdit }: DeliveriesTabProps) => {
  const client = useApolloClient();
  const [page, setPage] = useState(1);
  // Local, not in the URL: a filter is not worth sharing and the rows it
  // selects are customer personal data.
  const [filter, setFilter] = useState(CapacityPlanDeliveryFilter.All);
  const [editing, setEditing] = useState<{
    id: number;
    editor: DeliveryEditor;
  } | null>(null);
  const [latitudeDraft, setLatitudeDraft] = useState('');
  const [longitudeDraft, setLongitudeDraft] = useState('');
  const [addressDraft, setAddressDraft] =
    useState<DeliveryAddressDraft>(emptyAddressDraft);

  const [setDeliveryCoordinates, { loading: isSavingCoordinates }] =
    useSetCapacityPlanDeliveryCoordinatesMutation({
      refetchQueries: affectedQueries,
    });

  const [setDeliveryAddress, { loading: isSavingAddress }] =
    useSetCapacityPlanDeliveryAddressMutation({
      refetchQueries: affectedQueries,
    });

  const isSaving = isSavingCoordinates || isSavingAddress;

  const closeEditor = () => {
    setEditing(null);
    setLatitudeDraft('');
    setLongitudeDraft('');
    setAddressDraft(emptyAddressDraft);
  };

  // A different Run means a different set of rows, so start over.
  useEffect(() => {
    setPage(1);
    setFilter(CapacityPlanDeliveryFilter.All);
    setEditing(null);
  }, [run.id]);

  const { data, previousData, loading, error } = useCapacityPlanDeliveriesQuery(
    {
      variables: {
        runId: run.id,
        page,
        pageSize: deliveriesPageSize,
        filter,
      },
      // The client's default policy ignores GraphQL errors, which would show a
      // refused query as a run with no rows at all.
      errorPolicy: 'all',
    },
  );

  // The Address Processing Flow answers asynchronously, so the counters and the
  // rows are pulled again while anything is still pending.
  const { isPolling } = usePolledQuery({
    fetchFn: async () => {
      // `refetchQueries` hands back the refetches it started; awaiting them is
      // what makes one tick finish only once the fresh data is in the cache.
      const refreshed = client.refetchQueries({ include: polledQueries });
      await Promise.all(refreshed.results);
    },
    // A tick refetching the rows mid-save would re-render the row under the
    // editor, so polling waits for the save to settle.
    shouldPoll: run.pendingGeocodes > 0 && !isSaving,
    // Apollo writes the refreshed queries into the cache itself; there is no
    // extra state to keep here.
    onData: () => {},
    intervalMs: geocodingPollIntervalMs,
  });

  // The previous page stays on screen while the next one is fetched, so a
  // filter or page change does not flash the empty state.
  const deliveries =
    data?.capacityPlanDeliveries ?? previousData?.capacityPlanDeliveries;
  const totalCount = deliveries?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / deliveriesPageSize));
  const firstOnPage =
    totalCount === 0 ? 0 : (page - 1) * deliveriesPageSize + 1;
  const lastOnPage = Math.min(totalCount, page * deliveriesPageSize);

  // The server refuses a pin change while the solve is on its way, because it
  // reads the coordinates as they are when it starts.
  const runBusy = isRunBusy(run);
  const canEditRows = canEdit && !runBusy;

  const applyFilter = (value: CapacityPlanDeliveryFilter) => {
    setFilter(value);
    setPage(1);
    closeEditor();
  };

  const openPage = (nextPage: number) => {
    setPage(nextPage);
    // The row being edited is not on the next page, and an editor left open
    // would save its coordinates onto whatever row takes its place.
    closeEditor();
  };

  const handleEditCoordinates = (row: CapacityPlanDeliveryRow) => {
    setEditing({ id: row.id, editor: 'coordinates' });
    setLatitudeDraft(String(row.latitude ?? ''));
    setLongitudeDraft(String(row.longitude ?? ''));
  };

  const handleSaveCoordinates = async () => {
    if (editing === null) {
      return;
    }

    const converted = parseDeliveryCoordinates(latitudeDraft, longitudeDraft);
    if (!converted.ok) {
      toast.error(converted.message);
      return;
    }

    try {
      const { data: saved } = await setDeliveryCoordinates({
        variables: {
          input: {
            runId: run.id,
            deliveryId: editing.id,
            latitude: converted.latitude,
            longitude: converted.longitude,
          },
        },
      });
      // A refused change (run queued or computing, run gone) comes back in
      // the payload rather than as a thrown error.
      const errors = saved?.setCapacityPlanDeliveryCoordinates?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0].message ?? 'The coordinates were not saved.');
        return;
      }
      closeEditor();
      toast.success('The coordinates were saved.');
    } catch {
      toast.error('The coordinates were not saved.');
    }
  };

  const handleEditAddress = (row: CapacityPlanDeliveryRow) => {
    setEditing({ id: row.id, editor: 'address' });
    setAddressDraft({
      address: row.address ?? '',
      town: row.town ?? '',
      village: row.village ?? '',
      county: row.county ?? '',
    });
  };

  const handleSaveAddress = async () => {
    if (editing === null) {
      return;
    }

    if (isBlankAddress(addressDraft)) {
      toast.error('Fill in the address before saving.');
      return;
    }

    try {
      const { data: saved } = await setDeliveryAddress({
        variables: {
          input: {
            runId: run.id,
            deliveryId: editing.id,
            ...addressDraft,
          },
        },
      });
      const errors = saved?.setCapacityPlanDeliveryAddress?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0].message ?? 'The address was not saved.');
        return;
      }
      closeEditor();
      const validationError =
        saved?.setCapacityPlanDeliveryAddress
          ?.capacityPlanDeliveryAddressPayload?.validationError;
      if (validationError) {
        toast.warning(
          `The address was saved, but the row is still invalid: ${validationError}`,
        );
        return;
      }
      toast.success('The address was saved.');
    } catch {
      toast.error('The address was not saved.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-4 gap-4">
        <KpiCard label="Total" value={run.totalDeliveries} />
        <KpiCard label="Valid" value={run.validDeliveries} tone="positive" />
        <KpiCard
          label="Invalid rows"
          value={run.invalidDeliveries}
          tone={run.invalidDeliveries > 0 ? 'danger' : 'default'}
          sub={
            run.invalidDeliveries > 0
              ? 'fix with Edit address, or in the file and create a new run'
              : undefined
          }
        />
        <KpiCard
          label="Geocoding"
          value={`${run.geocodedDeliveries}/${run.totalDeliveries}`}
          sub={`${run.pendingGeocodes} pending · ${run.failedGeocodes} failed${
            isPolling ? ' · refreshes every 5 s' : ''
          }`}
          progress={{
            segments: [
              {
                fraction: share(run.geocodedDeliveries, run.totalDeliveries),
                className: 'bg-design-green',
              },
              {
                fraction: share(run.failedGeocodes, run.totalDeliveries),
                className: 'bg-darker-red',
              },
            ],
          }}
        />
      </div>

      <DeliveryIssuesCard run={run} canEdit={canEdit} onShow={applyFilter} />

      {runBusy && (
        <InfoBox variant="default" icon={<InfoIcon />}>
          The plan is queued or computing, so the coordinates and addresses of
          this run cannot be changed until it finishes.
        </InfoBox>
      )}

      {run.outOfCountryDeliveries > 0 && (
        <InfoBox variant="warning" icon={<AlertIcon />}>
          Deliveries outside the country are excluded from Compute. Move their
          pin inside the country with Set coordinates to plan them again.
        </InfoBox>
      )}

      {error && (
        <InfoBox variant="danger" icon={<AlertIcon />}>
          The deliveries of this run could not be loaded. {error.message}
        </InfoBox>
      )}

      <FilterChips
        ariaLabel="Filter deliveries"
        value={filter}
        onChange={applyFilter}
        options={[
          {
            value: CapacityPlanDeliveryFilter.All,
            label: 'All',
            count: run.totalDeliveries,
          },
          {
            value: CapacityPlanDeliveryFilter.FailedGeocode,
            label: 'Failed geocoding',
            count: run.failedGeocodes,
          },
          {
            value: CapacityPlanDeliveryFilter.OutOfCountry,
            label: 'Outside the country',
            count: run.outOfCountryDeliveries,
          },
          {
            value: CapacityPlanDeliveryFilter.Invalid,
            label: 'Invalid rows',
            count: run.invalidDeliveries,
          },
          {
            value: CapacityPlanDeliveryFilter.Approximated,
            label: 'Approximated',
            count: run.approximatedGeocodes,
          },
          {
            value: CapacityPlanDeliveryFilter.PendingGeocode,
            label: 'Pending',
            count: run.pendingGeocodes,
          },
          {
            value: CapacityPlanDeliveryFilter.MissingCoordinates,
            label: 'Missing coordinates',
            count: run.missingCoordinates,
          },
        ]}
      />

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-end gap-2 text-sm text-light-black dark:text-sublte-white">
          {loading && <span className="text-xs">Loading…</span>}
          <span>{`Showing ${firstOnPage}–${lastOnPage} of ${totalCount}`}</span>
        </div>
        <div className="flex">
          <GenericTable
            framed
            model={createDeliveriesTableModel({
              canEditRows,
              editing,
              latitudeDraft,
              longitudeDraft,
              addressDraft,
              isSaving,
              onEdit: handleEditCoordinates,
              onEditAddress: handleEditAddress,
              onCancel: closeEditor,
              onSave:
                editing?.editor === 'address'
                  ? handleSaveAddress
                  : handleSaveCoordinates,
              onLatitudeChange: setLatitudeDraft,
              onLongitudeChange: setLongitudeDraft,
              onAddressChange: (field, value) =>
                setAddressDraft((draft) => ({ ...draft, [field]: value })),
            })}
            elements={deliveries?.items ?? []}
            rowKey={(row) => row.id}
            pageNumber={page}
            totalElements={totalPages}
            pageChanged={openPage}
          />
        </div>
      </div>
    </div>
  );
};

export default DeliveriesTab;
